import React from 'react';
import { Box, Button, FormControlLabel, IconButton, Switch, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { OBSIDIAN, OBSIDIAN_BODY_FONT } from '../../constants/obsidianDashboardTheme';
import type { OfficeHoursDayAvailability } from '../../utils/sandboxOfficeHoursApi';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export const officeHoursFieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: '#323232',
        color: OBSIDIAN.onSurface,
        '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
    },
    '& label': { color: OBSIDIAN.onSurfaceVariant },
    '& .MuiSelect-icon': { color: OBSIDIAN.onSurfaceVariant },
};

type Props = {
    value: OfficeHoursDayAvailability[];
    onChange: (next: OfficeHoursDayAvailability[]) => void;
};

/**
 * Meeteor-style weekly cadence rows (Obsidian styling) with optional $ per window.
 */
export function SandboxOfficeHoursWeeklyEditor({ value, onChange }: Props) {
    const byDay = (day: number) => value.find(d => d.day === day) || { day, enabled: false, slots: [] };

    const patchDay = (day: number, patch: Partial<OfficeHoursDayAvailability>) => {
        const next = value.map(d => (d.day === day ? { ...d, ...patch } : d));
        if (!next.some(d => d.day === day)) next.push({ day, enabled: false, slots: [], ...patch });
        onChange(next.sort((a, b) => a.day - b.day));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {ORDER.map(day => {
                const row = byDay(day);
                return (
                    <Box
                        key={day}
                        sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: `1px solid ${OBSIDIAN.outline}33`,
                            bgcolor: OBSIDIAN.surfaceContainer,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={row.enabled}
                                        onChange={e => {
                                            const enabled = e.target.checked;
                                            patchDay(day, {
                                                enabled,
                                                slots:
                                                    enabled && !row.slots.length
                                                        ? [{ start: '09:00', end: '17:00', price_amount: null }]
                                                        : row.slots,
                                            });
                                        }}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: OBSIDIAN.primary },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                bgcolor: OBSIDIAN.primary,
                                            },
                                        }}
                                    />
                                }
                                label={DAY_NAMES[day]}
                                sx={{
                                    m: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: OBSIDIAN.onSurface,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                    },
                                }}
                            />
                            {!row.enabled ? (
                                <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant }}>
                                    Unavailable
                                </Typography>
                            ) : null}
                        </Box>

                        {row.enabled
                            ? row.slots.map((slot, si) => (
                                  <Box
                                      key={`${day}-${si}`}
                                      sx={{
                                          mt: 1,
                                          display: 'grid',
                                          gridTemplateColumns: { xs: '1fr 1fr auto', sm: '1fr 1fr 72px auto' },
                                          gap: 1,
                                          alignItems: 'center',
                                      }}>
                                      <TextField
                                          size="small"
                                          type="time"
                                          value={slot.start}
                                          onChange={e => {
                                              const slots = [...row.slots];
                                              slots[si] = { ...slots[si], start: e.target.value };
                                              patchDay(day, { slots });
                                          }}
                                          sx={officeHoursFieldSx}
                                      />
                                      <TextField
                                          size="small"
                                          type="time"
                                          value={slot.end}
                                          onChange={e => {
                                              const slots = [...row.slots];
                                              slots[si] = { ...slots[si], end: e.target.value };
                                              patchDay(day, { slots });
                                          }}
                                          sx={officeHoursFieldSx}
                                      />
                                      <TextField
                                          size="small"
                                          placeholder="$"
                                          value={slot.price_amount == null ? '' : String(slot.price_amount)}
                                          onChange={e => {
                                              const raw = e.target.value.trim();
                                              const n = raw === '' ? null : Number(raw);
                                              const slots = [...row.slots];
                                              slots[si] = {
                                                  ...slots[si],
                                                  price_amount: n != null && Number.isFinite(n) ? n : null,
                                              };
                                              patchDay(day, { slots });
                                          }}
                                          sx={officeHoursFieldSx}
                                          inputProps={{ inputMode: 'decimal' }}
                                      />
                                      <IconButton
                                          size="small"
                                          disabled={row.slots.length <= 1}
                                          onClick={() => patchDay(day, { slots: row.slots.filter((_, i) => i !== si) })}
                                          sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                                          <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                  </Box>
                              ))
                            : null}

                        {row.enabled ? (
                            <Button
                                size="small"
                                onClick={() =>
                                    patchDay(day, {
                                        slots: [...row.slots, { start: '09:00', end: '17:00', price_amount: null }],
                                    })
                                }
                                sx={{
                                    mt: 1,
                                    textTransform: 'none',
                                    color: OBSIDIAN.primary,
                                    fontWeight: 600,
                                    fontSize: 12,
                                }}>
                                + Add time range
                            </Button>
                        ) : null}
                    </Box>
                );
            })}
            <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                $ blank = default price · 0 = free for that window
            </Typography>
        </Box>
    );
}
